<template>
  <v-dialog :value="value" max-width="600" persistent @input="$emit('input', $event)">
    <v-card v-if="falta">
      <v-card-title class="d-flex align-center justify-space-between">
        <span>Detalhes da Justificativa</span>
        <v-chip small :color="corStatus(falta.status)" dark>{{ falta.status }}</v-chip>
      </v-card-title>

      <v-divider />

      <v-card-text class="pt-4">
        <v-row dense>
          <v-col cols="12">
            <div class="caption grey--text text--darken-1">Colaborador</div>
            <div class="body-1 font-weight-medium">{{ falta.colaborador?.name || "—" }}</div>
          </v-col>

          <v-col cols="6">
            <div class="caption grey--text text--darken-1 mt-2">
              {{ falta.data_fim ? "Período da Falta" : "Data da Falta" }}
            </div>
            <div class="body-2">
              {{ formatarData(falta.data_inicio) }}
              <span v-if="falta.data_fim"> até {{ formatarData(falta.data_fim) }}</span>
            </div>
          </v-col>

          <v-col cols="6">
            <div class="caption grey--text text--darken-1 mt-2">Enviado em</div>
            <div class="body-2">{{ formatarDataHora(falta.created_at) }}</div>
          </v-col>

          <v-col cols="12">
            <div class="caption grey--text text--darken-1 mt-2">Motivo</div>
            <div class="body-2">{{ falta.motivo }}</div>
          </v-col>

          <v-col v-if="falta.observacao" cols="12">
            <div class="caption grey--text text--darken-1 mt-2">Observação do colaborador</div>
            <div class="body-2">{{ falta.observacao }}</div>
          </v-col>

          <v-col v-if="falta.documento" cols="12" class="mt-2">
            <v-btn outlined color="primary" small :loading="abrindoDocumento" @click="verDocumento">
              <v-icon left small>mdi-file-document-outline</v-icon>
              Ver Documento
            </v-btn>
          </v-col>
        </v-row>

        <template v-if="falta.status !== 'pendente'">
          <v-divider class="my-3" />
          <v-row dense>
            <v-col cols="6">
              <div class="caption grey--text text--darken-1">Avaliado por</div>
              <div class="body-2">{{ falta.avaliador?.name || "—" }}</div>
            </v-col>
            <v-col cols="6">
              <div class="caption grey--text text--darken-1">Avaliado em</div>
              <div class="body-2">{{ formatarDataHora(falta.avaliado_em) }}</div>
            </v-col>
            <v-col v-if="falta.observacao_avaliador" cols="12">
              <div class="caption grey--text text--darken-1 mt-1">Resposta</div>
              <div class="body-2">{{ falta.observacao_avaliador }}</div>
            </v-col>
          </v-row>
        </template>

        <template v-if="podeAvaliar">
          <v-divider class="my-3" />
          <div class="subtitle-2 mb-2">Avaliar Justificativa</div>
          <v-textarea
            v-model="observacao"
            label="Observação (opcional)"
            outlined
            dense
            rows="3"
            counter="500"
            :disabled="avaliando"
            :rules="[(v) => !v || v.length <= 500 || 'Máximo 500 caracteres']"
          />
        </template>
        <template v-else-if="falta.status === 'pendente'">
          <v-divider class="my-3" />
          <v-alert type="info" dense text class="mb-0">
            Você não pode avaliar uma justificativa que você mesmo enviou.
          </v-alert>
        </template>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="avaliando" @click="fechar">Fechar</v-btn>
        <template v-if="podeAvaliar">
          <v-btn
            color="red"
            dark
            :loading="avaliando === 'reprovado'"
            :disabled="!!avaliando || observacao.length > 500"
            @click="avaliar('reprovado')"
          >
            <v-icon left small>mdi-close-circle</v-icon>
            Reprovar
          </v-btn>
          <v-btn
            color="green"
            dark
            :loading="avaliando === 'aprovado'"
            :disabled="!!avaliando || observacao.length > 500"
            @click="avaliar('aprovado')"
          >
            <v-icon left small>mdi-check-circle</v-icon>
            Aprovar
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "FaltaJustificadaDetailDialog",
  props: {
    value: { type: Boolean, default: false },
    falta: { type: Object, default: null },
  },
  data() {
    return {
      observacao: "",
      avaliando: null,
      abrindoDocumento: false,
    };
  },
  computed: {
    ...mapGetters("Auth", ["user"]),
    podeAvaliar() {
      if (!this.falta || this.falta.status !== "pendente") return false;
      return this.user?.id !== this.falta.user_id;
    },
  },
  watch: {
    value(aberto) {
      if (aberto) this.observacao = "";
    },
  },
  methods: {
    fechar() {
      this.$emit("input", false);
    },
    corStatus(status) {
      return { pendente: "orange", aprovado: "green", reprovado: "red" }[status] || "grey";
    },
    formatarData(data) {
      if (!data) return "—";
      const [ano, mes, dia] = String(data).split("T")[0].split("-");
      return `${dia}/${mes}/${ano}`;
    },
    formatarDataHora(data) {
      if (!data) return "—";
      const d = new Date(data);
      return d.toLocaleString("pt-BR");
    },
    async verDocumento() {
      try {
        this.abrindoDocumento = true;
        await this.$services.colaboradorPontosService.abrirDocumentoFalta(this.falta.id);
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.abrindoDocumento = false;
      }
    },
    async avaliar(status) {
      try {
        this.avaliando = status;
        await this.$services.colaboradorPontosService.avaliarFaltaJustificada(this.falta.id, {
          status,
          observacao_avaliador: this.observacao || null,
        });
        this.$toast.success(
          status === "aprovado" ? "Justificativa aprovada!" : "Justificativa reprovada."
        );
        this.$emit("avaliado");
        this.fechar();
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.avaliando = null;
      }
    },
  },
};
</script>
